<template>
  <div class="oap-video">
    <!--  Optional slot for inner placeholder -->
    <div
      v-show="showPlaceholder"
      ref="videoPlaceholder"
      :key="placeholderBackgroundKey"
      v-background="placeholderBackgroundProxy"
      class="placeholder bg-placeholder"
      aria-hidden="true"
      :class="placeholderClass"
      :style="{
        zIndex: placeholderZIndex,
      }"
      @click="togglePlaying"
    ></div>
    <div v-if="hasSources && videoSrc" class="oap-video__container">
      <video
        :key="videoSrc"
        ref="video"
        src=""
        :data-src="videoSrc"
        :class="{
          '-wide': isVideoWide,
        }"
        v-bind="{ autoplay: isAnimationEnabled, 'data-autoplay': isAnimationEnabled }"
        :playsinline="playsinline"
        :loop="loop"
        :muted="muted"
        type="video/mp4"
        preload="metadata"
        @click="togglePlaying"
      ></video>
      <button
        v-if="controls"
        ref="controls"
        class="oap-video__control"
        :class="`oap-video__control--is-${controlsPosition}`"
        :aria-pressed="playing.toString()"
        :title="playButtonTitle"
      >
        <span class="oap-video__control-overlay">
          <img :src="playButtonSrc" :alt="playButtonTitle" />
        </span>
      </button>
    </div>
  </div>
</template>

<script>
import { AnalyticsHandler, intersectionViewportObserver, pushDataLayer } from '@Foundation';
import eventBus from '@refapp/eventbus-js';
import { makeVideoAccessible } from '../utilities/makeVideoAccessible';
import { mapGetters } from 'vuex';
import {
  HERO_VIDEO_UPDATE,
  HERO_VIDEO_PAUSE,
  HERO_VIDEO_TOGGLE_PLAYING,
  HERO_VIDEO_BACKGROUND_PLAYING,
} from '@Feature/Filter/code/Scripts/constants/eventNames';
import { uniqueId } from '../utilities/uniqueId';
import {
  ACCESSIBILITY_STORE_NAMESPACE,
  GET_SITEWIDE_ANIMATIONS,
} from '@Foundation/accessibilityStore/accessibilityStore';

const TUTORIAL_VIDEO = 'Tutorial';

export default {
  name: 'OapVideo',

  props: {
    sources: { type: Object, default: null },

    gaCategory: { type: String, default: '' },
    gaLabel: { type: String, default: '' },
    eventName: { type: String, default: 'video_interaction' },
    linkUrl: { type: String, default: '' },

    placeholderBackground: { type: Object, default: null },
    playButtonTitle: { type: String, default: 'play or pause video' },

    placeholderClass: { type: Array, default: () => [] },

    autoplay: { type: Boolean, default: true },
    playsinline: { type: Boolean, default: true },
    loop: { type: Boolean, default: true },
    muted: { type: Boolean, default: true },
    controls: { type: Boolean, default: true },
    controlsPosition: { type: String, default: 'top-right' },

    pauseIfLeftViewport: { type: Boolean, default: false },
    playIfIsInViewportOnMobile: { type: Boolean, default: false },
    isInformative: { type: Boolean, default: false },
    videoTitle: { type: String, default: '' },

    lazyLoadTrigger: {
      type: String,
      default: 'eager',
      validator: function (value) {
        return ['eager', 'click', 'intersecting'].includes(value);
      },
    },

    uniqueId: { type: String, default: '' },
  },

  data() {
    return {
      lgScreenMediaQuery: 721,
      playing: this.autoplay,
      forcePlay: false,
      isLazyLoaded: false,
      sourcesProxy: {},
      placeholderBackgroundProxy: {},
      placeholderBackgroundKey: uniqueId(),
      autoplayProxy: this.autoplay,
      showPlaceholder: true,
      shouldPlayAsBackground: true,
      placeholderZIndex: 0,
      progressMilestones: {
        25: false,
        50: false,
        75: false,
        100: false,
      },
    };
  },

  computed: {
    ...mapGetters('filterList', ['getFilterDataset']),
    isAnimationEnabled() {
      return this[GET_SITEWIDE_ANIMATIONS]() && this.autoplayProxy;
    },

    isVideoWide() {
      return this.videoSrc === this.filteredSourceProxies?.lg;
    },

    isLargeScreen() {
      return window.innerWidth > this.lgScreenMediaQuery;
    },

    videoSrc() {
      return this.isLargeScreen
        ? this.filteredSourceProxies?.lg
        : this.filteredSourceProxies?.sm + this.shouldShowFrame;
    },

    playButtonSrc() {
      return this.playing
        ? '/frontend/static/images/media-gallery-pause.svg'
        : '/frontend/static/images/media-gallery-play.svg';
    },

    hasSources() {
      return this.filteredSourceProxies && Object.keys(this.filteredSourceProxies)?.length;
    },

    filteredSourceProxies() {
      if (this.sourcesProxy && Object.keys(this.sourcesProxy).length) {
        return Object.entries(this.sourcesProxy).reduce(
          (acc, [k, v]) => (v ? { ...acc, [k]: v } : acc),
          {}
        );
      }
      return this.sources;
    },

    isOS() {
      return /iPad|iPhone|iPod/.test(navigator.userAgent);
    },

    // add fix for displaying video preview for iOs devices
    shouldShowFrame() {
      // sets #t=0.001 for iOS to display the first frame
      return this.isOS ? '#t=0.001' : '';
    },

    isTutorialVideo() {
      return this.gaLabel === TUTORIAL_VIDEO;
    },
  },

  watch: {
    playing(val) {
      val ? this.playVideo() : this.$refs.video.pause();
    },
    isAnimationEnabled(newValue) {
      if (!newValue) this.pauseEventHandler();
    },
  },

  created() {
    this.sourcesProxy = this.sources;
    this.placeholderBackgroundProxy = this.placeholderBackground;
    this.showPlaceholder = Boolean(
      this.placeholderBackgroundProxy && Object.keys(this.placeholderBackgroundProxy).length
    );

    if (this.placeholderBackground && !this.autoplay) this.placeholderZIndex = 1;
  },

  mounted() {
    if (this.sources === null || !Object.keys(this.sources).length) return;

    this.setupEventListeners();
    this.handleLazyLoad();
    this.setupIntersectionObservers();
    this.setupMobileViewportPlay();
    this.setupEventBusListeners();
    makeVideoAccessible(this.$refs.video, this.isInformative, this.videoTitle);
  },

  beforeUnmount() {
    if (this.$refs?.controls) {
      this.$refs?.controls?.removeEventListener('click', this.togglePlaying);
    }
    if (this.$refs?.video) {
      this.$refs.video.removeEventListener('timeupdate', this.handleVideoProgress);
      this.$refs.video.removeEventListener('play', this.onVideoProgress);
      this.$refs.video.removeEventListener('pause', this.onVideoProgress);
      if (!this.loop) {
        this.$refs.video.removeEventListener('waiting', this.onVideoProgress);
      }
      this.$refs.video.removeEventListener('ended', this.onVideoProgress);
    }
  },

  methods: {
    ...mapGetters(ACCESSIBILITY_STORE_NAMESPACE, [GET_SITEWIDE_ANIMATIONS]),

    setupEventListeners() {
      this.$refs?.controls?.addEventListener('click', this.togglePlaying);

      if (!this.loop) {
        this.$refs.video.addEventListener('ended', this.togglePlaying);
      }
      // Add timeupdate listener for progress tracking
      this.$refs.video.addEventListener('timeupdate', this.handleVideoProgress);

      // Add event listeners for video status tracking
      this.$refs.video.addEventListener('play', () => this.onVideoProgress('play'));
      this.$refs.video.addEventListener('pause', () => this.onVideoProgress('pause'));
      if (!this.loop) {
        this.$refs.video.addEventListener('waiting', () => this.onVideoProgress('buffering'));
      }
      this.$refs.video.addEventListener('ended', () => this.onVideoProgress('video completed'));
    },

    handleLazyLoad() {
      if (this.lazyLoadTrigger === 'eager') {
        this.load();
      }
    },

    setupIntersectionObservers() {
      this.setupPauseIfLeftViewport();
      this.setupLazyLoadIntersecting();
    },

    setupPauseIfLeftViewport() {
      if (!this.pauseIfLeftViewport) return;

      intersectionViewportObserver(this.$el, {
        checkIsVisible: true,
        delay: 300,
        rootMargin: '0px',
        threshold: 0,
        shouldUnobserve: false,
        conditionCheckCallback: this.intersectionConditionCheck,
        callbackBeforeResolve: this.pauseEventHandler,
      });
    },

    setupLazyLoadIntersecting() {
      if (this.lazyLoadTrigger !== 'intersecting') return;

      intersectionViewportObserver(this.$el, {
        checkIsVisible: true,
        delay: 300,
        rootMargin: '200px',
        threshold: 0,
        shouldUnobserve: true,
        callbackBeforeResolve: this.load,
      });
    },

    setupMobileViewportPlay() {
      this.$nextTick(() => {
        setTimeout(() => {
          if (this.shouldSetupMobileViewport()) {
            this.setupMobileVideoObserver();
          }
        }, 300);
      });
    },

    shouldSetupMobileViewport() {
      return this.playIfIsInViewportOnMobile && window.innerWidth < this.lgScreenMediaQuery;
    },

    setupMobileVideoObserver() {
      const videoContainer = this.$el.querySelector('.oap-video__container');
      intersectionViewportObserver(videoContainer, {
        threshold: 0.9,
        shouldUnobserve: false,
        conditionCheckCallback: (change) => change.isIntersecting,
        callbackBeforeResolve: this.playVideo,
      }).then(() => {
        if (this.isTutorialVideo) {
          this.onloadDataLayer();
        }
      });
    },

    setupEventBusListeners() {
      /* istanbul ignore next */
      eventBus.on(HERO_VIDEO_PAUSE, this.pauseEventHandler);
      eventBus.on('oap-video::pause', this.pauseEventHandler);
      eventBus.on(HERO_VIDEO_UPDATE, this.changeVideo);
      eventBus.on(
        `${HERO_VIDEO_TOGGLE_PLAYING + (this.uniqueId ? '::' + this.uniqueId : '')}`,
        this.togglePlaying
      );
      eventBus.on(HERO_VIDEO_BACKGROUND_PLAYING, this.handleBackgroundPlayingEvent);
    },

    handleBackgroundPlayingEvent(shouldPlay) {
      if (this.shouldPlayAsBackground) {
        shouldPlay ? this.$refs.video.play() : this.$refs.video.pause();
      } else {
        this.$refs.video.pause();
      }
    },

    load() {
      if (this.$refs.video) {
        this.$refs.video.src = this.$refs.video.dataset.src;
      }
      this.isLazyLoaded = true;
    },

    togglePlaying() {
      this.pushAnalytics();
      if (this.sources?.sm || this.sources?.lg) {
        this.playing = !this.playing;
        this.shouldPlayAsBackground = !this.shouldPlayAsBackground;

        if (this.placeholderBackground) {
          const zIndex = this.playing ? 1 : 0;
          this.placeholderZIndex = zIndex;
        }
      }
    },

    pauseEventHandler() {
      if (this.$refs.video && !this.forcePlay && this.playing) {
        this.$refs.video.pause();
        this.shouldPlayAsBackground = false;
        this.playing = false;

        this.$el.closest('.oap-attraction-hero')?.classList.add('-pause');
      }
    },

    playVideo() {
      if (!this.isLazyLoaded) {
        this.load();
      }
      this.forcePlay = true;
      eventBus.emit('oap-video::pause');
      this.forcePlay = false;
      this.showPlaceholder = false;
      this.$refs.video.play();
      this.playing = true;
      this.shouldPlayAsBackground = true;
    },

    changeVideo() {
      const banner = this.getFilterDataset?.banner;

      if (!banner) return;

      const { video, background } = banner;

      if (video && video?.sources) {
        this.sourcesProxy = video.sources;

        if (this.autoplayProxy) this.playing = true;
      } else {
        this.sourcesProxy = {};
        this.autoplayProxy = false;
      }

      if (background) {
        this.placeholderBackgroundProxy = background;
        this.placeholderBackgroundKey = uniqueId();
      }
      this.resetProgressMilestones();
    },
    resetProgressMilestones() {
      // Reset progress milestones when video changes
      this.progressMilestones = {
        25: false,
        50: false,
        75: false,
        100: false,
      };
    },

    intersectionConditionCheck(change) {
      return change.isIntersecting && this.playing && !this.autoplayProxy;
    },

    pushAnalytics() {
      const state = this.playing ? 'pause' : 'play';
      let tag = {
        event_name: 'video_interaction',
        type: 'userActionEvent',
        ecommerce: 'undefined',
        category: this.gaCategory,
        action: state,
        label: this.gaLabel,
      };

      if (this.isTutorialVideo) {
        tag.link_url = this.linkUrl;
      } else {
        tag.video_title = this.videoTitle;
        tag.video_action = state;
      }

      try {
        AnalyticsHandler.getAnalyticsHandler().push(tag);
      } catch (e) {
        /* istanbul ignore next */
        console.warn('Could not push to dataLayer', e);
      }
    },

    onloadDataLayer() {
      const tag = {
        event_name: this.eventName,
        type: 'nonInteractiveEvent',
        category: this.gaCategory,
        action: 'video started',
        label: this.gaLabel,
        link_url: this.linkUrl,
      };

      try {
        AnalyticsHandler.getAnalyticsHandler().push(tag);
      } catch (e) {
        /* istanbul ignore next */
        console.warn('Could not push to dataLayer', e);
      }
    },
    handleVideoProgress() {
      const video = this.$refs.video;
      if (!video || !video.duration) return;

      const percentComplete = (video.currentTime / video.duration) * 100;

      // Check each milestone
      [25, 50, 75, 100].forEach((milestone) => {
        if (percentComplete >= milestone && !this.progressMilestones[milestone]) {
          this.progressMilestones[milestone] = true;
          const action = milestone === 100 ? 'video completed' : `${milestone}% viewed`;
          this.onVideoProgress(action);
        }
      });
    },

    onVideoProgress(action) {
      let progressTag = {
        ecommerce: 'undefined',
        event: 'uaevent',
        event_name: 'video_interaction',
        eventCategory: 'video',
        eventAction: action, //(video completed | $X% viewed | pause | buffering | play)
        eventLabel: this.videoTitle || this.gaLabel, //[videoName] if the video has no name then use the title of the module
        video_title: this.videoTitle || this.gaLabel,
        video_action: action, //(video completed | $X% viewed | pause | buffering | play)
      };
      try {
        pushDataLayer(progressTag);
      } catch (e) {
        /* istanbul ignore next */
        console.warn('Could not push to dataLayer', e);
      }
    },
  },
};
</script>
